/**
 * Cart variant edit modal (ported from autel-shop, adapted for dealer mini-cart / cart-items).
 */
class CartEdit {
  constructor() {
    this.modal = document.getElementById('Cart-Edit-Modal');
    if (!this.modal) return;

    this.content = this.modal.querySelector('[data-cart-edit-content]');
    this.lineKey = null;
    this.lineIndex = null;
    this.quantity = 1;
    this.variants = [];
    this.selectedOptions = [];

    document.addEventListener('click', this.onDocumentClick.bind(this));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.modal.hasAttribute('hidden')) {
        this.close();
      }
    });
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-cart-edit]');
    if (trigger) {
      event.preventDefault();
      this.open(trigger);
      return;
    }

    if (event.target.closest('[data-cart-edit-close]')) {
      event.preventDefault();
      this.close();
    }
  }

  open(trigger) {
    if (trigger.classList.contains('loading')) return;

    const url = trigger.dataset.url;
    this.lineKey = trigger.dataset.key;
    this.lineIndex = trigger.dataset.line;
    this.quantity = parseInt(trigger.dataset.quantity, 10) || 1;

    if (!url) return;

    trigger.classList.add('loading');

    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const template = doc.querySelector('[data-cart-edit-template]');
        if (!template) throw new Error('Cart edit template missing');

        this.content.innerHTML = template.outerHTML;
        this.bindTemplate();
        this.modal.removeAttribute('hidden');
        document.body.classList.add('cart-edit-open');
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        trigger.classList.remove('loading');
      });
  }

  close() {
    this.modal.setAttribute('hidden', '');
    document.body.classList.remove('cart-edit-open');
    this.content.innerHTML = '';
  }

  bindTemplate() {
    const root = this.content.querySelector('[data-cart-edit-template]');
    const form = root.querySelector('[data-cart-edit-form]');
    const qtyInput = root.querySelector('[data-cart-edit-quantity]');
    const variantsScript = root.querySelector('[data-cart-edit-variants]');

    if (qtyInput) qtyInput.value = this.quantity;

    try {
      this.variants = JSON.parse(variantsScript.textContent);
    } catch (e) {
      this.variants = [];
    }

    this.selectedOptions = [];
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const checked = swatch.querySelector('input:checked');
      this.selectedOptions[parseInt(swatch.dataset.optionIndex, 10)] = checked ? checked.value : null;
    });

    root.querySelectorAll('[data-cart-edit-option]').forEach((input) => {
      input.addEventListener('change', () => {
        const position = parseInt(input.dataset.optionPosition, 10);
        this.selectedOptions[position] = input.value;
        const header = input.closest('.cart-edit__swatch').querySelector('[data-header-option]');
        if (header) header.textContent = input.value;
        this.updateVariantUI(root);
      });
    });

    this.updateVariantUI(root);
    form.addEventListener('submit', (event) => this.onSubmit(event, root));
  }

  findVariant() {
    return this.variants.find((variant) => {
      return this.selectedOptions.every((value, index) => {
        if (value == null) return true;
        return variant[`option${index + 1}`] === value;
      });
    });
  }

  getVariantStrings() {
    return window.variantStrings || {};
  }

  updateVariantUI(root) {
    const variant = this.findVariant();
    const variantInput = root.querySelector('[data-cart-edit-variant-id]');
    const priceEl = root.querySelector('[data-cart-edit-price]');
    const compareEl = root.querySelector('[data-cart-edit-compare]');
    const labelEl = root.querySelector('[data-cart-edit-option-label]');
    const imageEl = root.querySelector('[data-cart-edit-image]');
    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    const strings = this.getVariantStrings();

    this.disableUnavailableOptions(root);

    if (!variant) {
      if (submitBtn) {
        submitBtn.disabled = true;
        const label = submitBtn.querySelector('[data-cart-edit-submit-label]');
        if (label) label.textContent = strings.unavailable || 'Unavailable';
      }
      return;
    }

    if (variantInput) variantInput.value = variant.id;
    if (priceEl) priceEl.textContent = variant.price_formatted;
    if (labelEl) labelEl.textContent = variant.title;

    if (compareEl) {
      if (variant.compare_at_price_formatted) {
        compareEl.textContent = variant.compare_at_price_formatted;
        compareEl.classList.remove('hide');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hide');
      }
    }

    if (imageEl && variant.featured_image) {
      imageEl.src = variant.featured_image;
    }

    if (submitBtn) {
      submitBtn.disabled = !variant.available;
      const label = submitBtn.querySelector('[data-cart-edit-submit-label]');
      if (label) {
        label.textContent = variant.available
          ? strings.addToCart || 'Add to cart'
          : strings.outOfStock || strings.unavailable || 'Sold out';
      }
    }
  }

  disableUnavailableOptions(root) {
    root.querySelectorAll('.cart-edit__swatch').forEach((swatch) => {
      const optionIndex = parseInt(swatch.dataset.optionIndex, 10);
      swatch.querySelectorAll('.cart-edit__swatch-element').forEach((element) => {
        const value = element.dataset.value;
        const available = this.variants.some((variant) => {
          if (!variant.available) return false;
          if (variant[`option${optionIndex + 1}`] !== value) return false;
          return this.selectedOptions.every((selected, index) => {
            if (index === optionIndex || selected == null) return true;
            return variant[`option${index + 1}`] === selected;
          });
        });

        element.classList.toggle('soldout', !available);
        const input = element.querySelector('input');
        if (input) input.disabled = !available;
      });
    });
  }

  getCartHosts() {
    const hosts = [];
    const miniCart = document.querySelector('mini-cart');
    const cartItems = document.querySelector('cart-items:not(mini-cart)');

    if (miniCart && typeof miniCart.getSectionsToRender === 'function') {
      hosts.push(miniCart);
    }
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      hosts.push(cartItems);
    }

    return hosts;
  }

  getSectionsToRender() {
    const seen = new Set();
    const sections = [];

    this.getCartHosts().forEach((host) => {
      host.getSectionsToRender().forEach((section) => {
        const key = `${section.id}:${section.section}:${section.selector || ''}`;
        if (seen.has(key)) return;
        seen.add(key);
        sections.push(section);
      });
    });

    return sections;
  }

  getSectionNames() {
    return [...new Set(this.getSectionsToRender().map((section) => section.section))];
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const element = selector ? doc.querySelector(selector) : doc.querySelector('.shopify-section');
    return element ? element.innerHTML : '';
  }

  fetchSections() {
    const sectionNames = this.getSectionNames().join(',');
    if (!sectionNames) return Promise.resolve({ sections: {} });

    const url = `${window.location.pathname}?sections=${sectionNames}&v=${Date.now()}`;

    return fetch(url)
      .then((response) => response.json())
      .then((sections) => ({ sections }));
  }

  renderSections(parsedState) {
    const render = (state) => {
      if (!state || !state.sections) return;

      this.getSectionsToRender().forEach((section) => {
        const sectionHtml = state.sections[section.section];
        if (!sectionHtml) return;

        const container = document.getElementById(section.id);
        if (!container) return;

        const elementToReplace =
          (section.selector && container.querySelector(section.selector)) || container;
        const innerHTML = this.getSectionInnerHTML(
          sectionHtml,
          section.selector || '.shopify-section'
        );

        if (!innerHTML) return;
        elementToReplace.innerHTML = innerHTML;
      });

      const miniCart = document.querySelector('mini-cart');
      const cartItems = document.querySelector('cart-items:not(mini-cart)');
      const isCartPage = document.body.classList.contains('template-cart');

      if (miniCart) {
        miniCart.currentItemCount = Array.from(
          miniCart.querySelectorAll('[name="updates[]"]')
        ).reduce((total, quantityInput) => total + parseInt(quantityInput.value, 10), 0);

        if (!isCartPage && typeof miniCart.open === 'function') {
          miniCart.open();
        }
      }

      if (cartItems) {
        cartItems.classList.toggle(
          'is-empty',
          !cartItems.querySelectorAll('.js-cart-item').length
        );
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) {
          cartFooter.classList.toggle('is-empty', cartItems.classList.contains('is-empty'));
        }
      }
    };

    if (parsedState && parsedState.sections) {
      render(parsedState);
      return Promise.resolve();
    }

    return this.fetchSections().then(render);
  }

  onSubmit(event, root) {
    event.preventDefault();

    const submitBtn = root.querySelector('[data-cart-edit-submit]');
    if (!submitBtn || submitBtn.disabled || submitBtn.classList.contains('loading')) return;

    const variant = this.findVariant();
    const quantity = parseInt(root.querySelector('[data-cart-edit-quantity]').value, 10) || 1;
    if (!variant || !variant.available) return;

    submitBtn.classList.add('loading');
    submitBtn.setAttribute('aria-disabled', 'true');

    const spinner = submitBtn.querySelector('.button-overlay-spinner');
    if (spinner) spinner.classList.remove('hidden');

    const sections = this.getSectionNames();
    const sectionsUrl = window.location.pathname;
    const routes = window.routes || {};

    const removeBody = JSON.stringify({
      line: parseInt(this.lineIndex, 10),
      quantity: 0,
      sections,
      sections_url: sectionsUrl
    });

    fetch(routes.cart_change_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: removeBody
    })
      .then((response) => response.json())
      .then(() => {
        const formData = new FormData();
        formData.append('id', String(variant.id));
        formData.append('quantity', String(quantity));
        formData.append('sections', sections);
        formData.append('sections_url', sectionsUrl);

        return fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          },
          body: formData
        });
      })
      .then((response) => response.json())
      .then((parsedState) => {
        if (parsedState.status) {
          throw new Error(parsedState.description || 'Unable to update cart');
        }

        return this.renderSections(parsedState).then(() => {
          this.close();

          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'cart-edit',
              cartData: parsedState,
              variantId: variant.id
            });
          }
        });
      })
      .catch((error) => {
        console.error(error);
        this.fetchSections()
          .then((state) => this.renderSections(state))
          .then(() => this.close());
      })
      .finally(() => {
        submitBtn.classList.remove('loading');
        submitBtn.removeAttribute('aria-disabled');
        if (spinner) spinner.classList.add('hidden');
      });
  }
}

window.addEventListener('load', () => {
  new CartEdit();
});
